import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { heroSlides } from "@/mocks/movies";

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % heroSlides.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, []);

  const go = (next: number) => {
    setIndex((next + heroSlides.length) % heroSlides.length);
  };

  return (
    <section
      className="relative w-full h-[440px] md:h-[600px] overflow-hidden"
      onTouchStart={(e) => {
        touchStartX.current = e.touches[0].clientX;
      }}
      onTouchEnd={(e) => {
        if (touchStartX.current === null) return;
        const delta = e.changedTouches[0].clientX - touchStartX.current;
        if (delta > 50) go(index - 1);
        if (delta < -50) go(index + 1);
        touchStartX.current = null;
      }}
    >
      {heroSlides.map((slide, i) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={slide.backdrop}
            alt={`${slide.title} 剧照`}
            title={`${slide.title} - 热门影视推荐`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/85 via-primary-950/55 to-primary-950/20" />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950/70 via-transparent to-transparent" />

          <div className="absolute inset-0 flex items-center">
            <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
              <div className="max-w-xl">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-500/90 text-white text-xs font-medium">
                  <i className="ri-fire-fill" aria-hidden="true" />
                  {slide.genre} · 热门推荐
                </span>
                <h2 className="mt-4 font-heading font-black text-3xl md:text-5xl text-white leading-tight">
                  {slide.title}
                </h2>
                <p className="mt-3 text-sm md:text-base text-white/85 leading-relaxed line-clamp-3">
                  {slide.intro}
                </p>
                <div className="mt-5 flex items-center gap-3 flex-wrap">
                  <Link
                    to={`/detail/${slide.id}`}
                    className="inline-flex items-center gap-2 h-10 md:h-11 px-5 md:px-6 rounded-md bg-accent-500 hover:bg-accent-600 text-white text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-play-fill text-lg" aria-hidden="true" />
                    查看详情
                  </Link>
                  <span className="text-white/70 text-sm flex items-center gap-1 font-label">
                    <i className="ri-star-fill text-accent-400" aria-hidden="true" />
                    {slide.rating.toFixed(1)} 分
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}

      <button
        type="button"
        aria-label="上一张"
        onClick={() => go(index - 1)}
        className="hidden md:flex absolute left-4 md:left-6 top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center rounded-full bg-white/15 hover:bg-white/30 text-white backdrop-blur transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-2xl" aria-hidden="true" />
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={() => go(index + 1)}
        className="hidden md:flex absolute right-4 md:right-6 top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center rounded-full bg-white/15 hover:bg-white/30 text-white backdrop-blur transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-2xl" aria-hidden="true" />
      </button>

      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {heroSlides.map((slide, i) => (
          <button
            key={slide.id}
            type="button"
            aria-label={`切换到第 ${i + 1} 张`}
            onClick={() => go(i)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === index ? "w-8 bg-accent-500" : "w-4 bg-white/50 hover:bg-white/80"
            }`}
          />
        ))}
      </div>
    </section>
  );
}